import {
  useState, useCallback, useEffect, useRef,
} from 'react'
import { type TarotAnimationPhase } from '../TarotCardGreeting'
import { type SubscriptionDocuments } from './useOracleChatSubscription'

export const SHUFFLE_DURATION_MS = 2000

// Duration to remain in the 'landed' phase after handleTransitionEnd fires.
// Must exceed the 300ms CSS opacity transition on the landed card image
// (see _DivinationCardRenderer.scss: `transition: opacity 0.3s ease`) so the
// flip card stays mounted long enough to fully overlap the fade-in and avoid
// a visible flash at the flip-to-land hand-off.
const LANDED_HOLD_MS = 350

interface UseTarotCardAnimationOptions {
  active: boolean
  streamingDocuments: SubscriptionDocuments | null | undefined
  isStreaming?: boolean
  // When true, forces the animation back to idle and prevents any new phase
  // transitions until cleared. Used to suppress the pick/jiggle animation
  // while the reframe modal is open.
  suppress?: boolean
}

interface UseTarotCardAnimationResult {
  animationPhase: TarotAnimationPhase
  animFrontImageUrl: string | undefined
  handleTransitionEnd: () => void
  handleCardPicked: () => void
}

const findTarotDocument = (docs: SubscriptionDocuments | null | undefined) => {
  if (!docs) return null
  return docs.find((d) => d.tarot) ?? null
}

export const useTarotCardAnimation = (
  options: UseTarotCardAnimationOptions,
): UseTarotCardAnimationResult => {
  const {
    active, streamingDocuments, isStreaming = false, suppress = false,
  } = options

  const [animationPhase, setAnimationPhase] = useState<TarotAnimationPhase>(
    active && !suppress ? 'shuffling' : 'idle',
  )
  const [animFrontImageUrl, setAnimFrontImageUrl] = useState<string | undefined>(undefined)
  const [jiggleTick, setJiggleTick] = useState(0)

  const minJiggleElapsedRef = useRef(false)
  const frontImagePreloadedRef = useRef(false)
  const jiggleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Track the last pullId we animated to avoid re-triggering on the same document
  const lastAnimatedPullIdRef = useRef<string | null>(null)

  // When active becomes true and we're idle, start shuffling
  useEffect(() => {
    if (suppress) return
    if (active && animationPhase === 'idle') {
      // Don't restart the cycle for a pullId we've already animated.
      const tarotDoc = findTarotDocument(streamingDocuments)
      const pullId = tarotDoc?.tarot?.pullId
      if (pullId && pullId === lastAnimatedPullIdRef.current) return
      setAnimationPhase('shuffling')
    }
    if (!active && animationPhase === 'shuffling') {
      setAnimationPhase('idle')
    }
  }, [active, animationPhase, suppress, streamingDocuments])

  // Suppress: force back to idle and clear staged state. The doc-arrival and
  // shuffle-timer effects below check `suppress` so they won't re-trigger a
  // transition while the suppression is in effect.
  useEffect(() => {
    if (!suppress) return
    if (animationPhase !== 'idle') {
      setAnimationPhase('idle')
    }
    setAnimFrontImageUrl(undefined)
    lastAnimatedPullIdRef.current = null
  }, [suppress, animationPhase])

  // Start SHUFFLE_DURATION_MS timer when entering shuffling phase
  useEffect(() => {
    if (animationPhase !== 'shuffling') return undefined
    const timerId = global.setTimeout(() => {
      setAnimationPhase('pickingCard')
    }, SHUFFLE_DURATION_MS)
    return () => {
      global.clearTimeout(timerId)
    }
  }, [animationPhase])

  // Handle tarot document arrival
  useEffect(() => {
    if (suppress) return
    const tarotDoc = findTarotDocument(streamingDocuments)
    if (!tarotDoc) return

    // Skip if we already animated this exact pull
    const pullId = tarotDoc.tarot?.pullId
    if (pullId && pullId === lastAnimatedPullIdRef.current) return

    const frontUrl = tarotDoc.tarot?.objects?.[0]?.frontImageUrl
    if (frontUrl) {
      setAnimFrontImageUrl(frontUrl)
    }

    // Mark this pullId as observed regardless of current phase, so the effect
    // doesn't re-trigger pickingCard later when we return to idle.
    if (pullId) lastAnimatedPullIdRef.current = pullId

    if (animationPhase === 'shuffling') {
      // Shuffle timer will advance to pickingCard.
      return
    }

    if (animationPhase === 'idle') {
      // Clarifying card path: skip shuffle, go directly to pickingCard
      setAnimationPhase('pickingCard')
    }
  }, [animationPhase, streamingDocuments, suppress])

  // handleCardPicked: transition from pickingCard to jiggling
  const handleCardPicked = useCallback(() => {
    setAnimationPhase('jiggling')
  }, [])

  // Start min jiggle timer when entering jiggling phase
  useEffect(() => {
    if (animationPhase !== 'jiggling') return undefined
    minJiggleElapsedRef.current = false
    frontImagePreloadedRef.current = false
    jiggleTimerRef.current = global.setTimeout(() => {
      minJiggleElapsedRef.current = true
      setJiggleTick((t) => t + 1)
    }, 3000)
    return () => {
      if (jiggleTimerRef.current) {
        global.clearTimeout(jiggleTimerRef.current)
      }
    }
  }, [animationPhase])

  // Preload front image when jiggling and URL is available
  useEffect(() => {
    if (animationPhase !== 'jiggling' || !animFrontImageUrl) return
    const img = new Image()
    const markPreloaded = () => {
      frontImagePreloadedRef.current = true
      setJiggleTick((t) => t + 1)
    }
    img.onload = markPreloaded
    // Treat load errors as 'done' so a 404/CORS/DNS failure cannot stall the
    // animation indefinitely; the per-card <img> tag in DivinationCardRenderer
    // has its own onError fallback that shows a placeholder.
    img.onerror = markPreloaded
    img.src = animFrontImageUrl
  }, [animationPhase, animFrontImageUrl])

  // Transition to transitioning when all conditions are met:
  // min jiggle elapsed, front image preloaded, and streaming complete
  useEffect(() => {
    if (animationPhase !== 'jiggling') return
    if (minJiggleElapsedRef.current && frontImagePreloadedRef.current && !isStreaming) {
      setAnimationPhase('transitioning')
    }
  }, [animationPhase, jiggleTick, isStreaming])

  // Stay in 'landed' just long enough for the landed card's 300ms opacity
  // transition to complete (see _DivinationCardRenderer.scss).
  useEffect(() => {
    if (animationPhase !== 'landed') return undefined
    const timerId = global.setTimeout(() => {
      setAnimationPhase('idle')
      setAnimFrontImageUrl(undefined)
    }, LANDED_HOLD_MS)
    return () => {
      global.clearTimeout(timerId)
    }
  }, [animationPhase])

  const handleTransitionEnd = useCallback(() => {
    setAnimationPhase('landed')
  }, [])

  return {
    animationPhase,
    animFrontImageUrl,
    handleTransitionEnd,
    handleCardPicked,
  }
}

export type { UseTarotCardAnimationOptions, UseTarotCardAnimationResult }
